import ko from "knockout";
import { CCCEventList } from ".";
import Event, {
  EventFilter,
  sortFilter,
  onDemandEventFilter,
} from "../../core/interfaces/Event";
import SelectItem from "../../core/interfaces/SelectItem";
import {
  PageDirection,
  PaginationLink,
  SortOption,
} from "../../core/interfaces/Pagination";
import {
  formatDate,
  DateFormat,
  onElementReady,
  decodeHTMLEntities,
} from "../../core/utilities";
import { CCCSelectSingle } from "../forms/select-single";
import { CCCButton } from "../forms/button";

export default class EventListVM {
  $eventList: CCCEventList = null;
  events$: KnockoutObservableArray<Event>;
  getTitleFormat: (date: Date) => void = null;
  getPostFormat: (date: Date) => void = null;
  getDateFormat: (date: Date) => void = null;
  getLongDateFormat: (date: Date) => void = null;
  decodeHTMLEntities: (text: string) => string = null;
  listTitle$: KnockoutObservable<string>;
  noEvents: KnockoutComputed<boolean>;
  loading$: KnockoutObservable<boolean>;
  currentDate$: KnockoutObservable<Date>;
  firstDay: Date;
  lastDay: Date;
  today: Date;
  currentView$: KnockoutObservable<SelectItem>;
  dateFilter$: KnockoutObservable<Date>;
  dateFilterStart: Date;
  dateFilterEnd: Date;
  display$: KnockoutObservable<boolean>;
  isPaginationEnabled$: KnockoutObservable<boolean>;
  pageSize: number;
  currentPage: number;
  pagination$: KnockoutObservable<Map<string, PaginationLink>>;
  isLastPage$: KnockoutObservable<boolean>;
  displaySeparator: KnockoutComputed<string>;
  displayRSVP: (date: Date) => boolean = null;

  constructor($element: CCCEventList) {
    this.$eventList = $element;
    this.listTitle$ = ko.observable("Events");
    this.events$ = ko.observableArray([]);
    this.loading$ = ko.observable(false);
    this.currentDate$ = ko.observable();
    this.currentView$ = ko.observable();
    this.dateFilter$ = ko.observable();
    this.display$ = ko.observable(false);
    this.today = new Date();
    this.isPaginationEnabled$ = ko.observable(false);
    this.pageSize = -1;
    this.currentPage = -1;
    this.pagination$ = ko.observable(new Map());
    this.isLastPage$ = ko.observable(true);
    this.getTitleFormat = (date: Date) => {
      return formatDate(date, DateFormat.longDateTime);
    };
    this.getPostFormat = (date: Date) => {
      return formatDate(date, DateFormat.short);
    };
    this.getDateFormat = (date: Date) => {
      return formatDate(date);
    };
    this.getLongDateFormat = (date: Date) => {
      return formatDate(date, DateFormat.longDate);
    };
    this.noEvents = ko.computed({
      read: () => {
        return this.events$().length === 0 && !this.loading$();
      },
    });
    this.displayRSVP = (date: Date): boolean => {
      return date > this.today;
    };
    this.displaySeparator = ko.pureComputed(() => {
      return this.pagination$().has(PageDirection.previous) ||
        (this.pagination$().has(PageDirection.next) && !this.isLastPage$())
        ? "top-separator"
        : "";
    });
    this.currentDate$.subscribe((newValue: Date) => {
      this.firstDay = new Date(newValue.getFullYear(), newValue.getMonth(), 1);
      this.lastDay = new Date(
        newValue.getFullYear(),
        newValue.getMonth() + 1,
        0
      );
      this.lastDay.setHours(23, 59, 59);
    });
    this.events$.subscribe(() => {
      setTimeout(() => {
        onElementReady(this.$eventList.shadowDom, "[id*='rsvpSelect']", true)
          .then(($elements: Array<CCCSelectSingle>) => {
            this.$eventList.setupRSVPSelects($elements);
          })
          .catch((error) => {
            console.error(error);
          });
      }, 50);
      if (this.isPaginationEnabled$()) {
        onElementReady(this.$eventList.shadowDom, "#nextButton")
          .then(($button: CCCButton) => {
            this.$eventList.setupNextButton($button);
          })
          .catch((error) => {
            console.error(error);
          });
        onElementReady(this.$eventList.shadowDom, "#prevButton")
          .then(($button: CCCButton) => {
            this.$eventList.setupPreviousButton($button);
          })
          .catch((error) => {
            console.error(error);
          });
      }
    });
    this.currentView$.subscribe((newValue: SelectItem) => {
      if (this.isPaginationEnabled$()) {
        //reset page when switching between view options
        this.currentPage = 1;
        this.pagination$(new Map());
      }

      if (
        Object.values(onDemandEventFilter).includes(
          newValue.name as onDemandEventFilter
        )
      ) {
        // Call filterEventsByView for /on-demand tabs
        this.filterOnDemandEventsByView(newValue);
      } else if (newValue.id >= 0) {
        this.filterEvents(newValue);
      }
    });
    this.dateFilter$.subscribe((newValue: Date) => {
      this.dateFilterStart = new Date(
        newValue.getFullYear(),
        newValue.getMonth(),
        newValue.getDate(),
        0,
        0,
        0
      );
      this.dateFilterEnd = new Date(
        newValue.getFullYear(),
        newValue.getMonth(),
        newValue.getDate(),
        23,
        59,
        59
      );
    });
    this.decodeHTMLEntities = (text: string): string => {
      return decodeHTMLEntities(text);
    };
  }

  setData(events: Array<Event>, currentDate: Date, view: SelectItem): void {
    this.events$(events);
    this.currentDate$(currentDate);
    this.currentView$(view);
  }

  gotoPage(direction: PageDirection): void {
    this.currentPage = Number(this.pagination$().get(direction).page);
    this.filterEvents(this.currentView$());
  }

  private filterEvents(filter: SelectItem): void {
    this.listTitle$("Events");
    let startDate = "";
    let endDate = "";
    let sort = SortOption.asc;
    const myEvents = false;
    switch (filter.name) {
      case EventFilter.Upcoming:
        startDate = this.today.toISOString().split(".")[0];
        break;
      case EventFilter.Past:
        endDate = this.today.toISOString().split(".")[0];
        sort = SortOption.desc;
        break;
      case "": {
        const resetStartDate = new Date(this.dateFilterStart);
        resetStartDate.setUTCHours(0, 0, 0, 0);

        const resetEndDate = new Date(resetStartDate);
        resetEndDate.setUTCDate(resetEndDate.getUTCDate() + 1);
        resetEndDate.setUTCHours(0, 0, 0, 0);

        startDate = resetStartDate.toISOString().split(".")[0];
        endDate = resetEndDate.toISOString().split(".")[0];

        this.listTitle$(
          "Events for " + formatDate(this.dateFilterStart, DateFormat.short)
        );
        break;
      }
    }
    if (
      Object.values(EventFilter).includes(filter.name as EventFilter) ||
      filter.name === ""
    ) {
      this.$eventList.fetchEventsCallback(
        startDate,
        endDate,
        myEvents,
        sort,
        this.pageSize,
        this.currentPage
      );
      if (this.isPaginationEnabled$()) {
        this.$eventList.fetchEventsCallback(
          startDate,
          endDate,
          myEvents,
          sort,
          this.pageSize,
          this.currentPage + 1
        );
      }
    }
  }

  private async filterOnDemandEventsByView(selectedView: SelectItem) {
    console.log("filterEventsByView Called with: ", selectedView);

    try {
      const endDate = new Date().toISOString().split(".")[0];
      const startDate = "";
      this.loading$(true);

      const initialEvents = this.events$();
      let resolved = false;

      // One-time subscription to detect when events are loaded
      const eventsChanged = new Promise<Event[]>((resolve) => {
        const subscription = this.events$.subscribe((newEvents) => {
          if (!resolved && newEvents !== initialEvents) {
            resolved = true;
            subscription.dispose();
            resolve(newEvents || []);
          }
        });
      });

      if (typeof this.$eventList.fetchEventsCallback === "function") {
        await this.$eventList.fetchEventsCallback(
          startDate,
          endDate,
          false,
          SortOption.desc,
          this.pageSize,
          this.currentPage
        );
      }
      const fetchedEvents = await eventsChanged;
      let filteredEvents: Array<Event> = [];
      switch (selectedView.name) {
        case onDemandEventFilter.Onboarding:
          filteredEvents = fetchedEvents.filter((event) =>
            event.name.toLowerCase().includes("(o)")
          );
          break;
        case onDemandEventFilter.Implementation:
          filteredEvents = fetchedEvents.filter((event) =>
            event.name.toLowerCase().includes("(i)")
          );
          break;
        case onDemandEventFilter.Adoption:
          filteredEvents = fetchedEvents.filter((event) =>
            event.name.toLowerCase().includes("(a)")
          );
          break;
        case onDemandEventFilter.Success:
          filteredEvents = fetchedEvents.filter((event) =>
            event.name.toLowerCase().includes("(s)")
          );
          break;
        default:
          filteredEvents = fetchedEvents;
          break;
      }
      this.events$(filteredEvents);
      this.listTitle$(`${selectedView.name} Events`);
    } catch (e) {
      console.error("Error filtering events:", e);
    } finally {
      this.loading$(false);
    }
  }

  sortEventsByTime(sortingOption: SelectItem): void {
    const today = new Date();
    let sort = SortOption.asc;
    const filter = this.currentView$();
    let startDate, endDate;
    if (
      Object.values(onDemandEventFilter).includes(
        filter.name as onDemandEventFilter
      )
    ) {
      this.filterOnDemandEventsByView(filter);
      return;
    }
    const firstDayOfWeek = new Date(today);
    firstDayOfWeek.setDate(today.getDate() - today.getDay() + 1);
    const lastDayOfWeek = new Date(today);
    lastDayOfWeek.setDate(today.getDate() - today.getDay() + 7);

    switch (sortingOption.name) {
      case sortFilter.ThisWeek:
        startDate = new Date(firstDayOfWeek);
        startDate.setUTCHours(0, 0, 0, 0);
        endDate = new Date(lastDayOfWeek);
        endDate.setUTCHours(23, 59, 59, 999);

        if (filter.name === EventFilter.Upcoming) {
          startDate = new Date(today);
        } else if (filter.name === EventFilter.Past) {
          endDate = new Date(today);
        }
        break;
      case sortFilter.ThisMonth:
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        startDate.setUTCHours(0, 0, 0, 0);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        endDate.setUTCHours(23, 59, 59, 999);

        if (filter.name === EventFilter.Upcoming) {
          startDate = new Date(today);
          startDate.setUTCHours(0, 0, 0, 0);
        } else if (filter.name === EventFilter.Past) {
          endDate = new Date(today);
          endDate.setUTCHours(23, 59, 59, 999);
        }
        break;
      case sortFilter.NextMonth:
        if (filter.name === EventFilter.Upcoming) {
          startDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
          startDate.setUTCHours(0, 0, 0, 0);
          endDate = new Date(today.getFullYear(), today.getMonth() + 2, 0);
          endDate.setUTCHours(23, 59, 59, 999);
        } else {
          startDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
          endDate = startDate;
        }
        break;
      case sortFilter.All:
        if (filter.name === EventFilter.Upcoming) {
          startDate = today;
        } else if (filter.name === EventFilter.Past) {
          sort = SortOption.desc;
          endDate = today;
        }
        break;
      default:
        startDate = null;
        endDate = null;
        break;
    }

    this.$eventList.fetchEventsCallback(
      startDate ? startDate.toISOString().split(".")[0] : "",
      endDate ? endDate.toISOString().split(".")[0] : "",
      false,
      sort,
      this.pageSize,
      this.currentPage
    );
  }

  getFormattedDate(startDate: string, endDate: string): string {
    const fullDateRegex =
      /(\w+), (\w+) (\d{1,2}), (\d{4}) at (\d{1,2}):(\d{2}) (AM|PM)/;

    const parseDate = (date: string) => {
      const match = date.match(fullDateRegex);
      if (!match) throw new Error("Invalid date format");
      const [, dayOfWeek, month, day, year, hour, minutes, period] = match;
      return {
        dayOfWeek,
        month,
        day: parseInt(day, 10),
        year,
        hour: parseInt(hour, 10),
        minutes: parseInt(minutes, 10),
        period,
      };
    };

    const formatTime = (hour: number, minutes: number): string =>
      minutes ? `${hour}:${minutes.toString().padStart(2, "0")}` : `${hour}`;
    const start = parseDate(startDate);
    const end = parseDate(endDate);

    const sameDay =
      start.dayOfWeek === end.dayOfWeek &&
      start.month === end.month &&
      start.day === end.day &&
      start.year === end.year;

    const formattedStartTime = formatTime(start.hour, start.minutes);
    const formattedEndTime = formatTime(end.hour, end.minutes);

    if (sameDay) {
      const dash = start.minutes || end.minutes ? "-" : "-";
      return start.period === end.period
        ? `${start.dayOfWeek}, ${start.month} ${start.day} ${start.year}, ${formattedStartTime}${dash}${formattedEndTime} ${start.period}`
        : `${start.dayOfWeek}, ${start.month} ${start.day} ${start.year}, ${formattedStartTime} ${start.period} - ${formattedEndTime} ${end.period}`;
    }
    //remove year from the 2nd if different days
    return `${start.dayOfWeek}, ${start.month} ${start.day} ${start.year}, ${formattedStartTime} ${start.period} - ${end.dayOfWeek}, ${end.month} ${end.day}, ${formattedEndTime} ${end.period}`;
  }
}
